import React, { useRef } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import './index.scss';
import Battery from '@/components/Battery';
import { BusinessType } from '@/utils/enum';
import { HttpStatusCode } from '@/fetch/core/constant';
import MultiVehicleApi from '@/fetch/business/multiVehicle';
import { withAsyncLock } from '@/utils';
import VehicleIssueTypeBadge from '@/components/VehicleIssueTypeBadge';
import { VehicleIssueType } from '@/utils/types';
import { isAbnormalValue } from '@/utils';
import { VehicleSupplier } from '@/utils/constant';

export enum VehicleStatusEnum {
  IDLE = 'IDLE',
  WAITING = 'WAITING',
  TASKING = 'TASKING',
  OFFLINE = 'OFFLINE'
}

export enum VehicleScheduleStateEnum {
  SETOUT = 'SETOUT',
  RETURN = 'RETURN',
  ARRIVED = 'ARRIVED'
}

export const VehicleStatusInfo: Record<
  VehicleStatusEnum,
  { text: string; color: string }
> = {
  [VehicleStatusEnum.IDLE]: { text: '空闲中', color: 'rgba(18,179,93,1)' },
  [VehicleStatusEnum.WAITING]: { text: '待发车', color: 'rgba(60,110,240,1)' },
  [VehicleStatusEnum.TASKING]: { text: '任务中', color: 'rgba(255,119,0,1)' },
  [VehicleStatusEnum.OFFLINE]: { text: '离线', color: 'rgba(186,190,199,1)' }
};

export const SpiderWebMark = () => {
  return (
    <>
      <div className="spider-web-mark">
        <span>蛛网</span>
      </div>
    </>
  );
};

export interface VehicleInfo {
  id: string;
  name: string;
  address: string;
  status?: VehicleStatusEnum;
  businessType?: BusinessType;
  battery: number;
  progress?: string;
  vehicleName?: string;
  stationName?: string;
  power?: number;
  vehicleStatus?: string;
  traveledMileage?: string | number;
  totalMileage?: string | number;
  vehicleScheduleState?: VehicleScheduleStateEnum;
  nextStopName?: string;
  warnType: VehicleIssueType; // 车辆当前的状态（维修中/停用中/待验收）
  supplier: VehicleSupplier | null; // 供应商
}

const VehicleCard: React.FC<{
  vehicle: VehicleInfo;
  onCardClick?: (vehicle: VehicleInfo) => void;
  hideMileage?: boolean;
}> = ({ vehicle, onCardClick, hideMileage = false }) => {
  const vehicleApiRef = useRef<MultiVehicleApi>(new MultiVehicleApi());
  const navigator = useNavigate();
  const vehicleName = vehicle.vehicleName || '';
  const stationName = vehicle.stationName || '';
  const battery = vehicle.power || 0;
  const status = vehicle.vehicleStatus
    ? (vehicle.vehicleStatus as VehicleStatusEnum)
    : VehicleStatusEnum.IDLE;

  const scheduleState =
    vehicle.vehicleScheduleState as VehicleScheduleStateEnum;

  const saveVehicleName = (vehicleName: string) => {
    localStorage.setItem('vehicleName', vehicleName);
  };
  const InfoItem = ({
    className,
    content,
    prefix = '',
    tooltipContent,
    noTooltip = false
  }: {
    className: string;
    content: string;
    prefix?: string;
    tooltipContent?: string;
    noTooltip?: boolean;
  }) => {
    const displayContent = content || '-';
    const tooltipDisplayContent = tooltipContent || displayContent;

    return (
      <div className={`vehicle-info-simple ${className}`}>
        <div className="icon"></div>
        {tooltipDisplayContent && !noTooltip ? (
          <div className="text-content">
            {prefix}
            {displayContent}
          </div>
        ) : (
          <div className="text-content">
            {prefix}
            {displayContent}
          </div>
        )}
      </div>
    );
  };

  const renderMileageInfo = () => {
    if (hideMileage) return null;
    // 车辆供应商是文远和白犀牛，不支持里程计算，不显示里程信息
    if (
      vehicle?.supplier === VehicleSupplier.WERIDE ||
      vehicle?.supplier === VehicleSupplier.RINO
    )
      return null;
    // 如果车辆状态不是任务中，则不显示里程信息
    if (status !== VehicleStatusEnum.TASKING) return null;

    // 格式化里程数据，保留两位小数
    const formatMileage = (mileage: string | number = 21.1098222) => {
      const numValue =
        typeof mileage === 'string'
          ? parseFloat(mileage) / 1000
          : mileage / 1000;
      return numValue.toFixed(2);
    };

    const mileageContent = (
      <React.Fragment>
        <span className="traveled-mileage">
          {formatMileage(vehicle.traveledMileage)}
        </span>
        /{formatMileage(vehicle.totalMileage)}
        km
      </React.Fragment>
    );
    if (
      vehicle?.businessType &&
      vehicle?.businessType === BusinessType.SPIDER_WEB
    ) {
      return <></>;
    }
    return (
      <div className="vehicle-info-simple mileage-info">
        <div className="icon"></div>
        里程进度: {mileageContent}
      </div>
    );
  };

  const getCardClassName = (vehicleStatus: VehicleStatusEnum): string => {
    if (vehicleStatus === VehicleStatusEnum.OFFLINE) {
      return 'grey-card';
    } else {
      return 'red-card';
    }
  };

  const renderVehicleInfo = () => {
    if (
      scheduleState === VehicleScheduleStateEnum.SETOUT ||
      scheduleState === VehicleScheduleStateEnum.RETURN
    ) {
      return (
        <InfoItem
          className="go-to-info"
          content={vehicle.nextStopName || '-'}
          prefix="前往："
        />
      );
    } else {
      return (
        <InfoItem className="address-info" content={vehicle.address || '-'} />
      );
    }
  };

  const getVehicleBusinessType = withAsyncLock(async (vehicleName: string) => {
    const res = await vehicleApiRef.current.getVehicleBusinessType(vehicleName);
    if (res?.code === HttpStatusCode.Success) {
      res.data?.businessType === BusinessType.SPIDER_WEB
        ? navigator('/spider-web/singleVehicle?vehicleName=' + vehicleName)
        : navigator(
            '/general-transport/singleVehicle?vehicleName=' + vehicleName
          );
    }
  });

  return (
    <div
      className={`vehicle-card-simple ${getCardClassName(status)}`}
      clstag="transport_workbench|click-vehicle-card"
      data-qidian-ext={JSON.stringify({
        任务状态: VehicleStatusInfo[status] && VehicleStatusInfo[status].text
      })}
      onClick={() => {
        saveVehicleName(vehicleName);
        if (onCardClick) {
          onCardClick(vehicle);
          return;
        }
        // 调用接口，获取车辆业务类型，如果是蛛网，则跳转蛛网车辆详情页，如果是普通运输，则跳转普通运输车辆详情页
        getVehicleBusinessType(vehicleName);
      }}
    >
      <VehicleIssueTypeBadge issueType={vehicle.warnType} />
      <div className="vehicle-header-simple">
        <span className="vehicle-name-simple">{vehicleName}</span>
        <Battery percent={battery || 0} />
        <span
          style={{
            color:
              battery <= 20
                ? '#FC3737' // 红色
                : battery <= 40
                  ? '#E6A23C' // 黄色
                  : '#12B35D', // 绿色
            marginLeft: '8px'
          }}
        >
          {battery}%
        </span>
        <span
          className="status-text"
          style={{
            color:
              (VehicleStatusInfo[status] && VehicleStatusInfo[status].color) ||
              'rgba(186,190,199,1)'
          }}
        >
          {(VehicleStatusInfo[status] && VehicleStatusInfo[status].text) ||
            '未知状态'}
        </span>
      </div>

      {stationName && (
        <InfoItem
          className="station-info"
          content={stationName}
          noTooltip={true}
        />
      )}

      {renderVehicleInfo()}
      {renderMileageInfo()}

      {/* 车辆图标 */}
      <div
        className={classNames(
          'vehicle-icon',
          status === VehicleStatusEnum.OFFLINE ? 'grey-car' : 'red-car',
          !isAbnormalValue(vehicle.warnType) && 'vehicle-has-issue'
        )}
      ></div>
    </div>
  );
};

export default VehicleCard;
